import React from 'react';
import { ChevronRight, ChevronLeft } from 'lucide-react';

interface Section {
  id: string;
  label: string;
  icon: React.ElementType;
}

interface SectionNavigatorProps {
  sections: Section[];
  currentSection: string;
  onNavigate: (sectionId: string) => void;
}

export const SectionNavigator: React.FC<SectionNavigatorProps> = ({
  sections,
  currentSection,
  onNavigate
}) => {
  const currentIndex = sections.findIndex(s => s.id === currentSection);
  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex < sections.length - 1;

  const handlePrevious = () => {
    if (hasPrevious) {
      onNavigate(sections[currentIndex - 1].id);
    }
  };

  const handleNext = () => {
    if (hasNext) {
      onNavigate(sections[currentIndex + 1].id);
    }
  };

  return (
    <div className="flex items-center justify-between py-4 px-6 bg-gray-900/50 border-t border-b border-gray-700 sticky top-0 z-20 backdrop-blur-sm">
      <button
        onClick={handlePrevious}
        disabled={!hasPrevious}
        className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all ${
          hasPrevious
            ? 'text-blue-400 hover:bg-blue-500/10 hover:text-blue-300'
            : 'text-gray-600 cursor-not-allowed'
        }`}
      >
        <ChevronLeft className="h-4 w-4" />
        <span className="text-sm font-medium hidden sm:inline">Sezione Precedente</span>
      </button>

      <div className="flex items-center space-x-2">
        {sections.map((section, index) => {
          const isActive = section.id === currentSection;
          const isCompleted = index < currentIndex;

          return (
            <div key={section.id} className="flex items-center">
              <button
                onClick={() => onNavigate(section.id)}
                className={`relative p-2 rounded-full transition-all ${
                  isActive
                    ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/50'
                    : isCompleted
                    ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                    : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                }`}
                title={section.label}
              >
                <section.icon className="h-4 w-4" />
                {isCompleted && (
                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-gray-900" />
                )}
              </button>
              {index < sections.length - 1 && (
                <div
                  className={`w-8 h-0.5 mx-1 ${
                    isCompleted ? 'bg-green-500/50' : 'bg-gray-700'
                  }`}
                />
              )}
            </div>
          );
        })}
      </div>

      <button
        onClick={handleNext}
        disabled={!hasNext}
        className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all ${
          hasNext
            ? 'text-blue-400 hover:bg-blue-500/10 hover:text-blue-300'
            : 'text-gray-600 cursor-not-allowed'
        }`}
      >
        <span className="text-sm font-medium hidden sm:inline">Prossima Sezione</span>
        <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  );
};
